import {
  ClassificationTagBankRelationship,
  DeleteBankFundCompanyPlatformMappingResponse,
  DeleteClassificationTagBankRelationshipResponse,
  FundCompany,
  GetAllTransactionPlatformsSuccessPayload,
  GetAllUsersSuccessPayload,
  GetBankFundCompanyPlatformMappingSuccessPayload,
  GetClassificationTagDealNameHedgesSuccessPayload,
  GetClassificationTagLegalEntitiesSuccessPayload,
  GetFundClassTagEmailsSuccessPayload,
  GetFundLevelTradePricingsSuccessPayload,
  GetUserAuthorizationsSuccessPayload,
  GetUserInitiatorsSuccessPayload,
  GetWorkflowComapnyFiltersResponse,
  UpdateFundCompanySuccessPayload,
  WorkflowFilteringData,
} from '@/pages/FundLevelAdmin/types';

import { ActionType, getType } from 'typesafe-actions';

import * as fundLevelAdminActions from './actions';
import { fetchWorkflowCompanyFilters } from './thunk';

export type FundLevelAdminActionType = ActionType<typeof fundLevelAdminActions>;

export interface FundLevelAdminState {
  classificationTagsBankRelationships: {
    data: ClassificationTagBankRelationship[] | null;
    loading: boolean;
  };
  bankFundCompanyPlatformMapping: {
    data: GetBankFundCompanyPlatformMappingSuccessPayload | null;
    loading: boolean;
  };
  allTransactionPlatforms: {
    data: GetAllTransactionPlatformsSuccessPayload | null;
    loading: boolean;
  };
  allFundCompanies: {
    data: FundCompany[] | null;
    loading: boolean;
  };
  updateFundCompany: {
    data: UpdateFundCompanySuccessPayload | null;
    loading: boolean;
  };
  userInitiators: {
    data: GetUserInitiatorsSuccessPayload | null;
    loading: boolean;
  };
  userAuthorizations: {
    data: GetUserAuthorizationsSuccessPayload | null;
    loading: boolean;
  };
  fundClassTagEmails: {
    data: GetFundClassTagEmailsSuccessPayload | null;
    loading: boolean;
  };
  classificationTagLegalEntities: {
    data: GetClassificationTagLegalEntitiesSuccessPayload | null;
    loading: boolean;
  };
  classificationTagDealNameHedges: {
    data: GetClassificationTagDealNameHedgesSuccessPayload | null;
    loading: boolean;
  };
  fundLevelTradePricings: {
    data: GetFundLevelTradePricingsSuccessPayload | null;
    loading: boolean;
  };
  allUsers: {
    data: GetAllUsersSuccessPayload | null;
    loading: boolean;
  };
  workflowFiltering: {
    data: GetWorkflowComapnyFiltersResponse | null;
    loading: boolean;
  };
  deleteBankFundCompanyPlatformMapping: {
    data: DeleteBankFundCompanyPlatformMappingResponse | null;
    loading: boolean;
  };
  deleteClassificationTagBankRelationship: {
    data: DeleteClassificationTagBankRelationshipResponse | null;
    loading: boolean;
  };
}

const initialState: FundLevelAdminState = {
  classificationTagsBankRelationships: {
    data: [],
    loading: false,
  },
  bankFundCompanyPlatformMapping: {
    data: null,
    loading: false,
  },
  allTransactionPlatforms: {
    data: null,
    loading: false,
  },
  allFundCompanies: {
    data: null,
    loading: false,
  },
  updateFundCompany: {
    data: null,
    loading: false,
  },
  userInitiators: {
    data: null,
    loading: false,
  },
  userAuthorizations: {
    data: null,
    loading: false,
  },
  fundClassTagEmails: {
    data: null,
    loading: false,
  },
  classificationTagLegalEntities: {
    data: null,
    loading: false,
  },
  classificationTagDealNameHedges: {
    data: null,
    loading: false,
  },
  fundLevelTradePricings: {
    data: null,
    loading: false,
  },
  allUsers: {
    data: null,
    loading: false,
  },
  workflowFiltering: {
    data: null,
    loading: false,
  },
  deleteBankFundCompanyPlatformMapping: {
    data: null,
    loading: false,
  },
  deleteClassificationTagBankRelationship: {
    data: null,
    loading: false,
  },
};

const fundLevelAdminReducer = (
  state: FundLevelAdminState = initialState,
  action: FundLevelAdminActionType,
): FundLevelAdminState => {
  switch (action.type) {
    // classificationTagsBankRelationships
    case getType(
      fundLevelAdminActions.getClassificationTagsBankRelationships.request,
    ):
      return {
        ...state,
        classificationTagsBankRelationships: {
          ...state.classificationTagsBankRelationships,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.getClassificationTagsBankRelationships.success,
    ):
      return {
        ...state,
        classificationTagsBankRelationships: {
          data: action.payload.data, // <-- just store array
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.getClassificationTagsBankRelationships.failure,
    ):
      return {
        ...state,
        classificationTagsBankRelationships: {
          ...state.classificationTagsBankRelationships,
          loading: false,
        },
      };

    // bankFundCompanyPlatformMapping
    case getType(
      fundLevelAdminActions.getBankFundCompanyPlatformMapping.request,
    ):
      return {
        ...state,
        bankFundCompanyPlatformMapping: {
          ...state.bankFundCompanyPlatformMapping,
          loading: true,
        },
      };
    case getType(
      fundLevelAdminActions.getBankFundCompanyPlatformMapping.success,
    ):
      return {
        ...state,
        bankFundCompanyPlatformMapping: {
          data: action.payload,
          loading: false,
        },
      };
    case getType(
      fundLevelAdminActions.getBankFundCompanyPlatformMapping.failure,
    ):
      return {
        ...state,
        bankFundCompanyPlatformMapping: {
          ...state.bankFundCompanyPlatformMapping,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.getAllTransactionPlatforms.request):
      return {
        ...state,
        allTransactionPlatforms: {
          ...state.allTransactionPlatforms,
          loading: true,
        },
      };
    case getType(fundLevelAdminActions.getAllTransactionPlatforms.success):
      return {
        ...state,
        allTransactionPlatforms: {
          data: action.payload,
          loading: false,
        },
      };
    case getType(fundLevelAdminActions.getAllTransactionPlatforms.failure):
      return {
        ...state,
        allTransactionPlatforms: {
          ...state.allTransactionPlatforms,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.getAllFundCompanies.request):
      return {
        ...state,
        allFundCompanies: {
          ...state.allFundCompanies,
          loading: true,
        },
      };
    case getType(fundLevelAdminActions.getAllFundCompanies.success):
      return {
        ...state,
        allFundCompanies: {
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(fundLevelAdminActions.getAllFundCompanies.failure):
      return {
        ...state,
        allFundCompanies: {
          ...state.allFundCompanies,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagBankRelationship.request,
    ):
      return {
        ...state,
        classificationTagsBankRelationships: {
          ...state.classificationTagsBankRelationships,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagBankRelationship.success,
    ):
      return {
        ...state,
        classificationTagsBankRelationships: {
          loading: false,
          data: state.classificationTagsBankRelationships.data?.map((r) =>
            r.id === action.payload.id ? action.payload : r,
          ) ?? [action.payload],
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagBankRelationship.failure,
    ):
      return {
        ...state,
        classificationTagsBankRelationships: {
          ...state.classificationTagsBankRelationships,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.updateBankFundCompanyPlatformMapping.request,
    ):
      return {
        ...state,
        bankFundCompanyPlatformMapping: {
          ...state.bankFundCompanyPlatformMapping,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.updateBankFundCompanyPlatformMapping.success,
    ):
      return {
        ...state,
        bankFundCompanyPlatformMapping: {
          loading: false,
          data: action.payload, // store { data: { id }, message }
        },
      };

    case getType(
      fundLevelAdminActions.updateBankFundCompanyPlatformMapping.failure,
    ):
      return {
        ...state,
        bankFundCompanyPlatformMapping: {
          ...state.bankFundCompanyPlatformMapping,
          loading: false,
        },
      };
    case getType(fundLevelAdminActions.updateFundCompany.request):
      return {
        ...state,
        updateFundCompany: {
          ...state.updateFundCompany,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.updateFundCompany.success):
      return {
        ...state,
        updateFundCompany: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateFundCompany.failure):
      return {
        ...state,
        updateFundCompany: {
          ...state.updateFundCompany,
          loading: false,
        },
      };

    // User Initiators
    case getType(fundLevelAdminActions.getUserInitiators.request):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.getUserInitiators.success):
      return {
        ...state,
        userInitiators: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.getUserInitiators.failure):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.createUserInitiator.request):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.createUserInitiator.success):
      return {
        ...state,
        userInitiators: {
          data: state.userInitiators.data
            ? {
                items: [
                  ...state.userInitiators.data.items,
                  action.payload.userInitiator,
                ],
              }
            : { items: [action.payload.userInitiator] },
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.createUserInitiator.failure):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateUserInitiator.request):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.updateUserInitiator.success):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateUserInitiator.failure):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.deleteUserInitiator.request):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.deleteUserInitiator.success):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.deleteUserInitiator.failure):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: false,
        },
      };

    // User Authorizations
    case getType(fundLevelAdminActions.getUserAuthorizations.request):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.getUserAuthorizations.success):
      return {
        ...state,
        userAuthorizations: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.getUserAuthorizations.failure):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.createUserAuthorization.request):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.createUserAuthorization.success):
      return {
        ...state,
        userAuthorizations: {
          data: state.userAuthorizations.data
            ? {
                items: [
                  ...state.userAuthorizations.data.items,
                  action.payload.userAuthorization,
                ],
              }
            : { items: [action.payload.userAuthorization] },
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.createUserAuthorization.failure):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateUserAuthorization.request):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.updateUserAuthorization.success):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateUserAuthorization.failure):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.deleteUserAuthorization.request):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.deleteUserAuthorization.success):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.deleteUserAuthorization.failure):
      return {
        ...state,
        userAuthorizations: {
          ...state.userAuthorizations,
          loading: false,
        },
      };

    //  -------------------------------
    //  Fund Class Tag Email Reducer Cases
    //  -------------------------------

    case getType(fundLevelAdminActions.getFundClassTagEmails.request):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.getFundClassTagEmails.success):
      return {
        ...state,
        fundClassTagEmails: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.getFundClassTagEmails.failure):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.createFundClassTagEmail.request):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.createFundClassTagEmail.success):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.createFundClassTagEmail.failure):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateFundClassTagEmail.request):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.updateFundClassTagEmail.success):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateFundClassTagEmail.failure):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.deleteFundClassTagEmail.request):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.deleteFundClassTagEmail.success):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.deleteFundClassTagEmail.failure):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.bulkCreateFundClassTagEmails.request):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.bulkCreateFundClassTagEmails.success):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.bulkCreateFundClassTagEmails.failure):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.bulkDeleteFundClassTagEmails.request):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.bulkDeleteFundClassTagEmails.success):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.bulkDeleteFundClassTagEmails.failure):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.mapEmailToMultipleTags.request):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.mapEmailToMultipleTags.success):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.mapEmailToMultipleTags.failure):
      return {
        ...state,
        fundClassTagEmails: {
          ...state.fundClassTagEmails,
          loading: false,
        },
      };

    //  -------------------------------
    //  Classification Tag Legal Entity Reducer Cases
    //  -------------------------------

    case getType(
      fundLevelAdminActions.getClassificationTagLegalEntities.request,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.getClassificationTagLegalEntities.success,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.getClassificationTagLegalEntities.failure,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.createClassificationTagLegalEntity.request,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.createClassificationTagLegalEntity.success,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.createClassificationTagLegalEntity.failure,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagLegalEntity.request,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagLegalEntity.success,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagLegalEntity.failure,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagLegalEntity.request,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagLegalEntity.success,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagLegalEntity.failure,
    ):
      return {
        ...state,
        classificationTagLegalEntities: {
          ...state.classificationTagLegalEntities,
          loading: false,
        },
      };

    //  -------------------------------
    //  Classification Tag Deal Name Hedge Reducer Cases
    //  -------------------------------

    case getType(
      fundLevelAdminActions.getClassificationTagDealNameHedges.request,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.getClassificationTagDealNameHedges.success,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.getClassificationTagDealNameHedges.failure,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.createClassificationTagDealNameHedge.request,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.createClassificationTagDealNameHedge.success,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.createClassificationTagDealNameHedge.failure,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagDealNameHedge.request,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagDealNameHedge.success,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.updateClassificationTagDealNameHedge.failure,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagDealNameHedge.request,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagDealNameHedge.success,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagDealNameHedge.failure,
    ):
      return {
        ...state,
        classificationTagDealNameHedges: {
          ...state.classificationTagDealNameHedges,
          loading: false,
        },
      };

    // Fund Level Trade Pricings
    case getType(fundLevelAdminActions.getFundLevelTradePricings.request):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.getFundLevelTradePricings.success):
      return {
        ...state,
        fundLevelTradePricings: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.getFundLevelTradePricings.failure):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.createFundLevelTradePricing.request):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.createFundLevelTradePricing.success):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.createFundLevelTradePricing.failure):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateFundLevelTradePricing.request):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.updateFundLevelTradePricing.success):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.updateFundLevelTradePricing.failure):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.deleteFundLevelTradePricing.request):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.deleteFundLevelTradePricing.success):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.deleteFundLevelTradePricing.failure):
      return {
        ...state,
        fundLevelTradePricings: {
          ...state.fundLevelTradePricings,
          loading: false,
        },
      };

    // --- Get All Users ---
    case getType(fundLevelAdminActions.getAllUsers.request):
      return {
        ...state,
        allUsers: {
          ...state.allUsers,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.getAllUsers.success):
      return {
        ...state,
        allUsers: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(fundLevelAdminActions.getAllUsers.failure):
      return {
        ...state,
        allUsers: {
          ...state.allUsers,
          loading: false,
        },
      };

    // Workflow Filtering
    case getType(fundLevelAdminActions.getWorkflowCompanyFilters.request):
      return {
        ...state,
        workflowFiltering: {
          ...state.workflowFiltering,
          loading: true,
        },
      };

    case getType(fundLevelAdminActions.getWorkflowCompanyFilters.success): {
      const rawItems: WorkflowFilteringData[] = Array.isArray(action.payload)
        ? (action.payload as unknown as WorkflowFilteringData[])
        : action.payload.data;
      return {
        ...state,
        workflowFiltering: {
          data: {
            data: rawItems.map((item) => ({
              ...item,
              id: `${item.user_id}_${item.fund_company_id}`,
            })),
          },
          loading: false,
        },
      };
    }

    case getType(fundLevelAdminActions.getWorkflowCompanyFilters.failure):
      return {
        ...state,
        workflowFiltering: {
          ...state.workflowFiltering,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.deleteBankFundCompanyPlatformMapping.request,
    ):
      return {
        ...state,
        deleteBankFundCompanyPlatformMapping: {
          ...state.deleteBankFundCompanyPlatformMapping,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.deleteBankFundCompanyPlatformMapping.success,
    ):
      return {
        ...state,
        deleteBankFundCompanyPlatformMapping: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.deleteBankFundCompanyPlatformMapping.failure,
    ):
      return {
        ...state,
        deleteBankFundCompanyPlatformMapping: {
          ...state.deleteBankFundCompanyPlatformMapping,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagBankRelationship.request,
    ):
      return {
        ...state,
        deleteClassificationTagBankRelationship: {
          ...state.deleteClassificationTagBankRelationship,
          loading: true,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagBankRelationship.success,
    ):
      return {
        ...state,
        deleteClassificationTagBankRelationship: {
          data: action.payload,
          loading: false,
        },
      };

    case getType(
      fundLevelAdminActions.deleteClassificationTagBankRelationship.failure,
    ):
      return {
        ...state,
        deleteClassificationTagBankRelationship: {
          ...state.deleteClassificationTagBankRelationship,
          loading: false,
        },
      };

    default:
      return state;
  }
};

export default fundLevelAdminReducer;
